/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Credentials } from '@opengeoweb/api';
import { getHeaders, handleResponse } from '@opengeoweb/api';
import type { ViewPreset } from '../../store/viewPresets/types';
import type { ViewPresetListItem } from '../../store/workspace/types';
import { getIdFromUrl, getPresetBaseUrl } from '../utils';

export const getViewPreset = async (
  viewpresetId?: string,
  auth?: Credentials,
): Promise<ViewPreset> => {
  if (!viewpresetId) {
    return undefined as unknown as ViewPreset;
  }
  return fetch(`${getPresetBaseUrl()}/viewpreset/${viewpresetId}`, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<ViewPreset>;
};

export const getViewPresets = async (
  auth?: Credentials,
): Promise<ViewPresetListItem[]> => {
  const url = `${getPresetBaseUrl()}/viewpreset`;
  return fetch(url, {
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<ViewPresetListItem[]>;
};

export const updateViewPreset = async (
  viewpresetId: string,
  data: ViewPreset,
  auth?: Credentials,
): Promise<null> =>
  fetch(`${getPresetBaseUrl()}/viewpreset/${viewpresetId}`, {
    headers: getHeaders(auth),
    method: 'PUT',
    body: JSON.stringify(data),
  }).then(handleResponse) as Promise<null>;

export const saveViewPreset = async (
  viewPresetId: string,
  data: ViewPreset,
  auth?: Credentials,
): Promise<void> => {
  const payload =
    data.initialProps === null ? { ...data, initialProps: {} } : data;

  return fetch(`${getPresetBaseUrl()}/viewpreset/${viewPresetId}/`, {
    headers: getHeaders(auth),
    method: 'PUT',
    body: JSON.stringify(payload),
  }).then(handleResponse) as Promise<void>;
};

export const saveViewPresetAs = async (
  data: ViewPreset,
  auth?: Credentials,
): Promise<string> => {
  const payload =
    data.initialProps === null ? { ...data, initialProps: {} } : data;
  const response = await fetch(`${getPresetBaseUrl()}/viewpreset/`, {
    headers: getHeaders(auth),
    method: 'POST',
    body: JSON.stringify(payload),
  });
  await handleResponse(response);
  const headerLocation =
    response.headers.get('Location') ?? response.headers.get('location');
  return getIdFromUrl(headerLocation!);
};

export const deleteViewPreset = async (
  viewPresetId: string,
  auth: Credentials,
): Promise<Headers | null> =>
  fetch(`${getPresetBaseUrl()}/viewpreset/${viewPresetId}`, {
    method: 'DELETE',
    headers: getHeaders(auth),
  }).then(handleResponse) as Promise<Headers | null>;
